import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import api from 'services/api';

export default function ForceChangePassword() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validatePw = (pw) => {
    if (!pw || pw.length < 8) return 'Au moins 8 caractères';
    if (!/[A-Z]/.test(pw)) return 'Au moins une lettre majuscule';
    if (!/[a-z]/.test(pw)) return 'Au moins une lettre minuscule';
    if (!/[0-9]/.test(pw)) return 'Au moins un chiffre';
    return null;
  };

  const pwError = validatePw(newPassword);
  const match = newPassword && confirmPassword && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (pwError) return setError(pwError);
    if (newPassword !== confirmPassword) return setError('Les mots de passe ne correspondent pas');

    setSaving(true);
    try {
      await api.post('/auth/force-change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      updateUser({ doit_changer_mot_de_passe: false });
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du changement');
    }
    setSaving(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        padding: '2.5rem 2rem',
        maxWidth: 440,
        width: '100%',
        margin: '1rem',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(139,26,26,0.08)', margin: '0 auto 1rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>
          <h2 style={{ margin: 0, color: '#1a1a1a', fontSize: '1.3rem' }}>Changement de mot de passe requis</h2>
          <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 8 }}>
            Bienvenue <strong>{user?.prenom || user?.nom}</strong>. Pour des raisons de sécurité,
            veuillez définir votre mot de passe personnel.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#444', marginBottom: 4 }}>
              Mot de passe temporaire (actuel)
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                style={{ ...inputStyle, paddingRight: 40 }}
                required
                autoFocus
              />
              <EyeToggle visible={showCurrent} onToggle={() => setShowCurrent(v => !v)} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#444', marginBottom: 4 }}>
              Nouveau mot de passe
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                style={{ ...inputStyle, paddingRight: 40 }}
                placeholder="Min. 8 car., 1 maj., 1 min., 1 chiffre"
                required
              />
              <EyeToggle visible={showNew} onToggle={() => setShowNew(v => !v)} />
            </div>
            {newPassword && (
              <div style={{ marginTop: 6, fontSize: '0.8rem' }}>
                <Rule ok={newPassword.length >= 8} text="8 caractères minimum" />
                <Rule ok={/[A-Z]/.test(newPassword)} text="Une majuscule" />
                <Rule ok={/[a-z]/.test(newPassword)} text="Une minuscule" />
                <Rule ok={/[0-9]/.test(newPassword)} text="Un chiffre" />
              </div>
            )}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#444', marginBottom: 4 }}>
              Confirmer le nouveau mot de passe
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                style={{ ...inputStyle, paddingRight: 40 }}
                required
              />
              <EyeToggle visible={showConfirm} onToggle={() => setShowConfirm(v => !v)} />
            </div>
            {confirmPassword && !match && (
              <div style={{ color: '#DC2626', fontSize: '0.8rem', marginTop: 4 }}>
                Les mots de passe ne correspondent pas
              </div>
            )}
          </div>

          {error && (
            <div style={{
              background: 'rgba(220,38,38,0.06)', color: '#DC2626',
              padding: '10px 14px', borderRadius: 8, fontSize: '0.85rem', marginBottom: 16,
              border: '1px solid rgba(220,38,38,0.15)',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={saving || !currentPassword || !match || !!pwError}
            style={{
              width: '100%', padding: '12px', border: 'none', borderRadius: 10,
              background: (saving || !currentPassword || !match || !!pwError) ? '#ccc' : '#8B1A1A',
              color: '#fff', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            {saving ? 'Enregistrement...' : 'Définir mon mot de passe'}
          </button>

          <button
            type="button"
            onClick={logout}
            style={{
              width: '100%', padding: '10px', border: 'none', borderRadius: 10,
              background: 'transparent', color: '#666', fontSize: '0.85rem',
              cursor: 'pointer', marginTop: 8,
            }}
          >
            Se déconnecter
          </button>
        </form>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 14px', border: '1.5px solid #ddd',
  borderRadius: 8, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box',
};

function EyeToggle({ visible, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      tabIndex={-1}
      style={{
        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
        background: 'none', border: 'none', cursor: 'pointer', padding: 4,
        display: 'flex', color: '#999', opacity: 0.7,
      }}
    >
      {visible ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );
}

function Rule({ ok, text }) {
  return (
    <div style={{ color: ok ? '#1A8754' : '#999', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
      <span>{ok ? '\u2713' : '\u25CB'}</span> {text}
    </div>
  );
}
